//@ts-check
import {isStartDateLessThanCurrentDate} from '@/helpers/dateUtils'
import {COUNTRY_CODES} from '@/utils/constants/countryConstants'
import {PRODUCT_REVIEW_TAB_INDEX} from '../../productReviewConstants'
import {getProductReviewFieldsForCountry, PRODUCT_REVIEW_TAB_SECTIONS} from '../ProductReviewTabs/productReviewDataMap'

/**
 * Checks whether a field value should be treated as empty for required-field validation.
 * @param {unknown} value Field value to check.
 * @returns {boolean} True when the value is considered empty.
 */
const isEmptyValue = value => {
  if (value === null || value === undefined) return true
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  return false
}

/**
 * Validates required fields for a list of product review sections against product data.
 * Only fields marked `required: true` or `requiredWhen` conditions are checked. Fields marked `localized: true` are
 * validated once per provided language, keyed by language code.
 *
 * @param {import('../ProductReviewTabs/productReviewDataMap').ProductReviewSectionConfig[]} sections Sections to validate (typically the ones rendered on the current tab).
 * @param {Object} productData Editable product data from the Product Review context.
 * @param {Array<{code: string, language: string}>} [languages] Available languages, used for localized fields.
 * @param {string} [countryCode] Current country code.
 * @returns {Object.<string, boolean>} Map of error keys (`source.key` or `source.key.languageCode`) to `true`.
 */
export const validateProductReviewFields = (sections = [], productData = {}, languages = [], countryCode) => {
  /** @type {Object.<string, boolean>} */
  const errors = {}

  sections.forEach(section => {
    getProductReviewFieldsForCountry(section.fields, countryCode).forEach(field => {
      let isRequired = field.required

      // Check conditional requirement
      if (field.requiredWhen) {
        // @ts-expect-error The field path is a runtime configuration value.
        const [source, key] = field.requiredWhen.field.split('.')
        const fieldValue = productData?.[source]?.[key]
        isRequired = fieldValue === field.requiredWhen.value
      }

      if (!isRequired) return

      const sourceData = productData?.[field.source] ?? {}

      if (field.localized) {
        languages.forEach(language => {
          const value = sourceData?.[field.key]?.[language.code]

          if (isEmptyValue(value)) {
            errors[`${field.source}.${field.key}.${language.code}`] = true
          }
        })
        return
      }

      const value = sourceData?.[field.key]

      if (isEmptyValue(value)) {
        errors[`${field.source}.${field.key}`] = true
      }
    })
  })

  return errors
}

/** Validation message shown when Display Size ID is zero, negative, or not a whole number. */
export const DISPLAY_SIZE_ID_ERROR = 'Display Size ID must be a positive number'

/**
 * Checks whether a Display Size ID value fails validation. The field is non-mandatory, so empty
 * values are valid; when provided, the value must be a positive integer (not zero or negative).
 * @param {unknown} value Display Size ID value to check.
 * @returns {boolean} True when the value is present but invalid.
 */
const isInvalidDisplaySizeId = value => {
  if (isEmptyValue(value)) return false

  const numericValue = Number(value)

  return !Number.isInteger(numericValue) || numericValue <= 0
}

/**
 * Returns the validation message for a Display Size ID value, or `undefined` when valid.
 * @param {unknown} value Display Size ID value to check.
 * @returns {string|undefined} Error message when invalid, otherwise `undefined`.
 */
export const getDisplaySizeIdError = value => (isInvalidDisplaySizeId(value) ? DISPLAY_SIZE_ID_ERROR : undefined)

/**
 * Validates the `display.displaySizeId` field: when present, it must be a positive integer.
 * @param {Object} productData Editable product data from the Product Review context.
 * @returns {Object.<string, boolean>} Map with `display.displaySizeId` set to `true` when invalid.
 */
export const validateDisplaySizeId = (productData = {}) => {
  /** @type {Object.<string, boolean>} */
  const errors = {}

  if (isInvalidDisplaySizeId(productData?.display?.displaySizeId)) {
    errors['display.displaySizeId'] = true
  }

  return errors
}

/** Media types that may never occupy the first media slot (`displayOrder` 1). */
const FIRST_SLOT_DISALLOWED_MEDIA_TYPES = ['VIDEO']

/**
 * Validates the Media tab: the first slot (`displayOrder` 1) can never hold a video -
 * dragging one there is allowed in the UI, but Save stays disabled until the arrangement
 * is fixed. An empty media list is valid (all images may be deleted).
 *
 * @param {Object} productData Editable product data from the Product Review context.
 * @returns {Object.<string, boolean>} Map with a `media` error key set to `true` when a
 *   video occupies the first slot.
 */
export const validateMediaFields = (productData = {}) => {
  /** @type {Object.<string, boolean>} */
  const errors = {}
  const media = productData?.media

  if (!Array.isArray(media) || media.length === 0) {
    return errors
  }

  const firstSlotItem = media.find(item => Number(item?.displayOrder) === 1)

  if (FIRST_SLOT_DISALLOWED_MEDIA_TYPES.includes(firstSlotItem?.mediaType)) {
    errors.media = true
  }

  return errors
}

/**
 * Returns a map of subcategory index to `true` for every item whose trimmed code is duplicated.
 * Empty codes are not counted as duplicates (missing codes are handled by required-field checks).
 *
 * @param {Array<{code?: string|null}>} [subCategories] Subcategory items.
 * @returns {Object.<number, boolean>} Map of subcategory index to duplicate flag.
 */
export const getDuplicateSubCategoryCodes = (subCategories = []) => {
  const codeCounts = {}

  subCategories.forEach(item => {
    const code = typeof item?.code === 'string' ? item.code.trim() : ''
    if (code) codeCounts[code] = (codeCounts[code] || 0) + 1
  })

  /** @type {Object.<number, boolean>} */
  const duplicates = {}

  subCategories.forEach((item, index) => {
    const code = typeof item?.code === 'string' ? item.code.trim() : ''
    if (code && codeCounts[code] > 1) {
      duplicates[index] = true
    }
  })

  return duplicates
}

/**
 * Validates subcategories on the Category section: when `category.subCategoryFlag` is enabled,
 * every subcategory requires a unique, non-empty code and a non-empty value for each language.
 *
 * @param {Object} productData Editable product data from the Product Review context.
 * @param {Array<{code: string, language: string}>} [languages] Available languages.
 * @returns {Object.<string, boolean>} Map of error keys to `true`.
 */
export const validateSubCategoryFields = (productData = {}, languages = []) => {
  /** @type {Object.<string, boolean>} */
  const errors = {}
  const category = productData?.category ?? {}

  if (!category?.subCategoryFlag) {
    return errors
  }

  const subCategories = Array.isArray(category?.subCategories) ? category.subCategories : []
  const duplicateCodes = getDuplicateSubCategoryCodes(subCategories)

  subCategories.forEach((item, index) => {
    const code = typeof item?.code === 'string' ? item.code.trim() : ''

    if (!code || duplicateCodes[index]) {
      errors[`category.subCategories.${index}.code`] = true
    }

    languages.forEach(language => {
      if (isEmptyValue(item?.values?.[language.code])) {
        errors[`category.subCategories.${index}.values.${language.code}`] = true
      }
    })
  })

  return errors
}

/**
 * Returns the diff in days between two ISO date strings (date1 - date2).
 * @param {string} date1
 * @param {string} date2
 * @returns {number}
 */
const dateDiffInDays = (date1, date2) => {
  const d1 = new Date(date1)
  const d2 = new Date(date2)
  d1.setHours(0, 0, 0, 0)
  d2.setHours(0, 0, 0, 0)
  return (d1.getTime() - d2.getTime()) / (1000 * 60 * 60 * 24)
}

/**
 * Validates the SKU start and end dates when `isNewProduct` is enabled.
 * Rules applied:
 *  - skuStartDate and skuEndDate must not be in the past (pastValidation)
 *  - skuStartDate must be before skuEndDate (startDate rule)
 *  - skuEndDate must be after skuStartDate (endDate rule)
 *
 * @param {Object} productData Editable product data from the Product Review context.
 * @returns {Object.<string, boolean>} Map of error keys to `true`.
 */
export const validateSkuDates = (productData = {}, countryCode) => {
  /** @type {Object.<string, boolean>} */
  const errors = {}
  const additionalInfo = productData?.additionalInfo ?? {}

  if (countryCode && countryCode !== COUNTRY_CODES.US) return errors

  if (!additionalInfo.isNewProduct) return errors

  const {skuStartDate, skuEndDate} = additionalInfo

  if (skuEndDate && dateDiffInDays(skuStartDate, skuEndDate) >= 0) {
    errors['additionalInfo.skuStartDate'] = true
  }

  if (skuEndDate && skuStartDate && dateDiffInDays(skuEndDate, skuStartDate) <= 0) {
    errors['additionalInfo.skuEndDate'] = true
  }

  return errors
}

/**
 * Per-tab custom validators. A tab listed here is validated by its custom function instead
 * of the generic required-field check. Add an entry here for any future tab-specific rules.
 * @type {Object.<number, (productData: Object, languages?: Array<{code: string, language: string}>, countryCode?: string) => Object.<string, boolean>>}
 */
const TAB_CUSTOM_VALIDATORS = {
  [PRODUCT_REVIEW_TAB_INDEX.MEDIA]: validateMediaFields,
  [PRODUCT_REVIEW_TAB_INDEX.BASE_DATA]: (productData, languages, countryCode) => ({
    ...validateProductReviewFields(
      PRODUCT_REVIEW_TAB_SECTIONS[PRODUCT_REVIEW_TAB_INDEX.BASE_DATA],
      productData,
      languages,
      countryCode
    ),
    ...validateSkuDates(productData, countryCode),
  }),
  [PRODUCT_REVIEW_TAB_INDEX.DISPLAY]: (productData, languages, countryCode) => ({
    ...validateProductReviewFields(
      PRODUCT_REVIEW_TAB_SECTIONS[PRODUCT_REVIEW_TAB_INDEX.DISPLAY],
      productData,
      languages,
      countryCode
    ),
    ...validateDisplaySizeId(productData),
  }),
  [PRODUCT_REVIEW_TAB_INDEX.BRAND_AND_CATEGORY]: (productData, languages, countryCode) => ({
    ...validateProductReviewFields(
      PRODUCT_REVIEW_TAB_SECTIONS[PRODUCT_REVIEW_TAB_INDEX.BRAND_AND_CATEGORY],
      productData,
      languages,
      countryCode
    ),
    ...validateSubCategoryFields(productData, languages),
  }),
}

/**
 * Validates the fields for a given Product Review tab. Tabs with a custom validator
 * (see `TAB_CUSTOM_VALIDATORS`, e.g. Media's first-slot video rule) use that instead
 * of the generic required-field validation applied to the tab's section configs.
 *
 * @param {number} tabIndex Selected Product Review tab index.
 * @param {Object} productData Editable product data from the Product Review context.
 * @param {Array<{code: string, language: string}>} [languages] Available languages, used for localized fields.
 * @param {string} [countryCode] Current country code.
 * @returns {Object.<string, boolean>} Map of error keys to `true`.
 */
export const validateProductReviewTab = (tabIndex, productData, languages, countryCode) => {
  const customValidator = TAB_CUSTOM_VALIDATORS[tabIndex]

  if (customValidator) {
    return customValidator(productData, languages, countryCode)
  }

  const sections = PRODUCT_REVIEW_TAB_SECTIONS[tabIndex] ?? []
  return validateProductReviewFields(sections, productData, languages, countryCode)
}
